import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Wrench,
  Zap,
  Home,
  Paintbrush,
  Trees,
  Droplets,
  X,
  Star,
  Phone,
  Mail,
  MapPin,
  Plus,
  UserCircle2,
  Briefcase,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  SmoothScroll,
  LineReveal,
  ScrollReveal,
  Magnetic,
} from "@/components/motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ---------- Data ---------- */
// Canonical trade/service list — used by Homepage, Pro signup, Pro profile edit,
// and the Guest quick-request form so dropdowns stay in lockstep.
const SERVICES = [
  "Plumbing",
  "Electrical",
  "HVAC",
  "Roofing",
  "Painting",
  "Landscaping",
  "Handyman",
  "Carpentry",
  "Flooring",
  "Cleaning",
  "Remodeling",
  "General Contractor",
  "Other",
];

// Alias retained for backward compatibility — same canonical list.
const TRADES = SERVICES;

const HERO_IMG_1 =
  "https://images.unsplash.com/photo-1707655536176-a2bc5e00cba9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxob21lJTIwc2VydmljZXMlMjBwbHVtYmluZyUyMGxhbmRzY2FwaW5nJTIwZWxlY3RyaWNpYW58ZW58MHx8fHwxNzgzNDgxNjI0fDA&ixlib=rb-4.1.0&q=85";
const HERO_IMG_2 =
  "https://images.unsplash.com/photo-1505798577917-a65157d3320a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxob21lJTIwc2VydmljZXMlMjBwbHVtYmluZyUyMGxhbmRzY2FwaW5nJTIwZWxlY3RyaWNpYW58ZW58MHx8fHwxNzgzNDgxNjI0fDA&ixlib=rb-4.1.0&q=85";

const SERVICE_IMGS = {
  Plumbing:
    "https://images.unsplash.com/photo-1454988501794-2992f706932e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxob21lJTIwc2VydmljZXMlMjBwbHVtYmluZyUyMGxhbmRzY2FwaW5nJTIwZWxlY3RyaWNpYW58ZW58MHx8fHwxNzgzNDgxNjI0fDA&ixlib=rb-4.1.0&q=85",
  Electrical:
    "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1400&q=80",
  HVAC:
    "https://images.unsplash.com/photo-1621905251918-48416bd8575a?auto=format&fit=crop&w=1400&q=80",
  Landscaping:
    "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1400&q=80",
  Roofing:
    "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=80",
  Painting:
    "https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1400&q=80",
};

const TESTIMONIAL_IMG_1 =
  "https://images.unsplash.com/photo-1611748058515-bbe9b98f7ae2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1ODh8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGhvbWVvd25lciUyMGhvdXNlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzgzNDgxNjI0fDA&ixlib=rb-4.1.0&q=85";
const TESTIMONIAL_IMG_2 =
  "https://images.unsplash.com/photo-1545946533-a2a28eeb0dc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA1ODh8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGhvbWVvd25lciUyMGhvdXNlJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzgzNDgxNjI0fDA&ixlib=rb-4.1.0&q=85";


/* ============================================================
 *  Hero — masked line reveal + parallax product photography
 * ============================================================ */
function Hero() {
  return (
    <section
      id="top"
      className="relative pt-12 sm:pt-16 md:pt-20 pb-16 sm:pb-24 overflow-hidden bg-[#0D1421]"
    >
      {/* Faint radial glow behind */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-[-10%] w-[720px] h-[720px] rounded-full bg-gradient-radial from-[#0E93EC]/12 via-transparent to-transparent blur-3xl" />
      </div>

      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 relative">
        {/* Meta strip */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center justify-between text-[10px] sm:text-[11px] uppercase tracking-[0.22em] sm:tracking-[0.25em] font-medium text-[#C9C6C6]/70 mb-8 sm:mb-14 gap-3"
        >
          <div className="flex items-center gap-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0E93EC] animate-pulse" />
            <span>Est. 2026 · Trusted local pros, nationwide</span>
          </div>
          <span className="font-mono-alt text-[#C9C6C6]/50 hidden sm:block">
            No. 001 / Home services · reimagined
          </span>
        </motion.div>

        <div className="max-w-5xl">
          <h1 className="font-editorial text-[13vw] md:text-[9vw] lg:text-[7.5rem] font-light leading-[0.9] tracking-[-0.04em] text-[#EEEEEF]">
            <LineReveal>Fix it.</LineReveal>
            <br />
            <LineReveal delay={0.3}>Build it.</LineReveal>
            <br />
            <span className="italic font-normal text-[#0E93EC] pr-2">
              <LineReveal delay={0.6}>Grow it.</LineReveal>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 sm:mt-10 max-w-md text-base sm:text-lg text-[#C9C6C6] leading-relaxed"
          >
            A quiet, nationwide network where homeowners meet the trades.
            No bidding wars. No per-lead fees. Just vetted local pros who
            show up.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.55, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 sm:mt-12 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 sm:gap-5"
          >
            <Magnetic strength={8}>
              <a
                href="#start"
                className="group inline-flex items-center bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-6 sm:pl-7 pr-4 sm:pr-5 text-base font-medium relative overflow-hidden w-full sm:w-auto justify-between sm:justify-start"
                data-testid="hero-start-cta"
              >
                <span className="mr-2">Get Started</span>
                <span className="inline-flex items-center justify-center w-9 h-9 bg-[#010204]/35 transition-transform group-hover:translate-x-1 shrink-0">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </a>
            </Magnetic>

            <a
              href="#start"
              className="group flex items-center gap-2 text-[#EEEEEF] text-sm font-medium border-b border-[#EEEEEF]/40 hover:border-[#EEEEEF] pb-1 transition-colors"
              data-testid="hero-choose-role"
            >
              <span className="uppercase tracking-[0.2em]">
                Choose your role
              </span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        {/* Bottom row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.0, duration: 1 }}
          className="mt-16 sm:mt-20 border-t border-[#EEEEEF]/12 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {[TESTIMONIAL_IMG_1, TESTIMONIAL_IMG_2, HERO_IMG_1].map((s, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full ring-2 ring-[#0D1421] bg-[#EEEEEF]/12 overflow-hidden"
                >
                  <img src={s} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
            <div className="text-sm text-[#C9C6C6]">
              <div className="flex items-center gap-1 text-[#0E93EC]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-current" />
                ))}
              </div>
              <div className="text-[13px]">Neighbors helping neighbors · nationwide</div>
            </div>
          </div>
          <div className="flex items-center gap-6 text-[11px] uppercase tracking-[0.2em] font-mono-alt text-[#C9C6C6]/60">
            <span>Scroll</span>
            <span className="w-8 h-px bg-[#C9C6C6]/30 relative overflow-hidden">
              <span className="absolute inset-0 bg-[#0E93EC] animate-[marquee-x_2s_ease-in-out_infinite]" />
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ============================================================
 *  TradesMarquee — continuous horizontal scroll of the 8 trades
 *  (panels taken from the approved TPWH header artwork)
 * ============================================================ */
const TRADE_CARDS = [
  { key: "roofing", name: "Roofing" },
  { key: "plumbing", name: "Plumbing" },
  { key: "hvac", name: "HVAC" },
  { key: "electrical", name: "Electrical" },
  { key: "painting", name: "Painting" },
  { key: "handyman", name: "Handyman" },
  { key: "drywall", name: "Drywall" },
  { key: "concrete", name: "Concrete" },
];

function TradesMarquee() {
  return (
    <section
      className="relative bg-[#010204] border-b border-[#0E93EC]/20 pt-8 sm:pt-10 pb-12 sm:pb-16 overflow-hidden"
      data-testid="trades-section"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 mb-6 sm:mb-8 flex items-baseline justify-between gap-4">
        <p className="text-[11px] sm:text-xs uppercase tracking-[0.25em] font-semibold text-[#0E93EC]">
          All Trades. One Hub.
        </p>
        <p className="hidden md:block text-[11px] uppercase tracking-[0.2em] text-[#C9C6C6]/60">
          Roofing · Plumbing · HVAC · Electrical · Painting · Handyman · Drywall · Concrete
        </p>
      </div>
      <div className="group relative">
        <div className="flex w-max gap-4 sm:gap-5 animate-trades-scroll group-hover:[animation-play-state:paused]">
          {[...TRADE_CARDS, ...TRADE_CARDS].map((t, i) => (
            <img
              key={`${t.key}-${i}`}
              src={`/brand/trades/${t.key}.png`}
              alt={t.name}
              className="w-60 sm:w-72 shrink-0 border border-[#0E93EC]/25"
              data-testid={i < TRADE_CARDS.length ? `trade-card-${t.key}` : undefined}
            />
          ))}
        </div>
        <div className="absolute inset-y-0 left-0 w-16 sm:w-24 bg-gradient-to-r from-[#010204] to-transparent pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 sm:w-24 bg-gradient-to-l from-[#010204] to-transparent pointer-events-none" />
      </div>
    </section>
  );
}

/* ============================================================
 *  ChoosePath — single consolidated role-picker section
 * ============================================================ */
function ChoosePath() {
  const roles = [
    {
      id: "guest",
      icon: UserCircle2,
      badge: "One-time request",
      title: "Guest",
      desc: "Just need one quick quote? Share your project — no account needed.",
      cta: "Submit a request",
      href: "#guest",
      accent: "bg-[#0E93EC]",
      textAccent: "text-[#0E93EC]",
    },
    {
      id: "homeowner",
      icon: Home,
      badge: "Track projects",
      title: "Homeowner",
      desc: "Create a free account, request up to 3 quotes, and manage every project in one dashboard.",
      cta: "Create account",
      href: "/homeowner/signup",
      alt: { label: "Existing? Log in", href: "/homeowner/login" },
      accent: "bg-[#0E93EC]",
      textAccent: "text-[#0E93EC]",
      highlight: true,
    },
    {
      id: "pro",
      icon: Briefcase,
      badge: "Grow your business",
      title: "Pro",
      desc: "Skip pay-per-lead. Set your service zones, get matched, and manage jobs from your Kanban.",
      cta: "Sign up as a Pro",
      href: "/pro/signup",
      alt: { label: "Existing? Pro login", href: "/pro/login" },
      accent: "bg-[#010204]",
      textAccent: "text-[#EEEEEF]",
    },
  ];

  return (
    <section id="start" className="py-16 sm:py-24 md:py-32 bg-[#0D1421]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12">
        <ScrollReveal className="text-center mb-10 sm:mb-14 max-w-3xl mx-auto">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-3 sm:mb-4">
            — Choose your path
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
            Who&apos;s here today?
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#C9C6C6] max-w-xl mx-auto leading-relaxed">
            Pick your role — we&apos;ll take you exactly where you need to go.
          </p>
        </ScrollReveal>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-6" data-testid="choose-path-grid">
          {roles.map((r, i) => {
            const Icon = r.icon;
            const isPrimary = r.highlight;
            return (
              <ScrollReveal
                key={r.id}
                delay={i * 0.08}
                className={`relative p-6 sm:p-8 md:p-9 flex flex-col ${
                  isPrimary
                    ? "bg-[#0E2F5F] text-[#EEEEEF] shadow-2xl md:-translate-y-3 border border-[#0E93EC]/50"
                    : "bg-[#010204] border border-[#C9C6C6]/10 text-[#EEEEEF]"
                }`}
                data-testid={`choose-path-${r.id}`}
              >
                <div className={`inline-flex items-center justify-center w-11 h-11 ${r.accent} text-white mb-5`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 ${isPrimary ? "text-[#0E93EC]" : r.textAccent}`}>
                  {r.badge}
                </div>
                <h3 className="font-editorial text-3xl sm:text-4xl font-light tracking-tighter leading-none">
                  {r.title}
                </h3>
                <p className={`mt-3 text-sm sm:text-[15px] leading-relaxed ${isPrimary ? "text-[#C9C6C6]" : "text-[#C9C6C6]"}`}>
                  {r.desc}
                </p>
                <div className="mt-6 flex flex-col gap-3 pt-4 border-t border-current/10">
                  {r.href.startsWith("#") ? (
                    <a
                      href={r.href}
                      className={`group inline-flex items-center justify-between h-12 px-4 font-medium ${
                        isPrimary
                          ? "bg-[#0E93EC] hover:bg-[#0E2F5F] text-white"
                          : "bg-[#0E93EC] hover:bg-[#0E2F5F] text-white"
                      }`}
                      data-testid={`choose-path-${r.id}-cta`}
                    >
                      <span>{r.cta}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </a>
                  ) : (
                    <Link
                      to={r.href}
                      className={`group inline-flex items-center justify-between h-12 px-4 font-medium ${
                        isPrimary
                          ? "bg-[#0E93EC] hover:bg-[#0E2F5F] text-white"
                          : "bg-[#0E93EC] hover:bg-[#0E2F5F] text-white"
                      }`}
                      data-testid={`choose-path-${r.id}-cta`}
                    >
                      <span>{r.cta}</span>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  )}
                  {r.alt && (
                    <Link
                      to={r.alt.href}
                      className={`text-xs uppercase tracking-[0.2em] font-medium underline-offset-4 hover:underline ${
                        isPrimary ? "text-[#EEEEEF]/70 hover:text-[#EEEEEF]" : "text-[#C9C6C6] hover:text-[#EEEEEF]"
                      }`}
                      data-testid={`choose-path-${r.id}-alt`}
                    >
                      {r.alt.label}
                    </Link>
                  )}
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 *  GuestQuickRequest — minimal 4-field form, no account
 * ============================================================ */
function GuestQuickRequest() {
  const [form, setForm] = useState({
    name: "",
    service_type: "",
    address: "",
    phone: "",
    agreed_to_disclaimer: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.service_type || !form.address || !form.phone) {
      toast.error("Please complete all four fields");
      return;
    }
    if (!form.agreed_to_disclaimer) {
      toast.error("Please acknowledge the connection-only disclaimer");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        service_type: form.service_type,
        address: form.address.trim(),
        phone: form.phone.trim(),
        is_guest: true,
        wants_multiple_quotes: false,
        agreed_to_disclaimer: true,
      });
      // Remember for Q4.A: pre-fill homeowner signup if they later create an account.
      try {
        localStorage.setItem(
          "wh_guest_prefill",
          JSON.stringify({ name: form.name.trim(), phone: form.phone.trim(), ts: Date.now() })
        );
      } catch {
        /* ignore quota */
      }
      setDone(true);
      setForm({ name: "", service_type: "", address: "", phone: "", agreed_to_disclaimer: false });
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) && d[0]?.msg ? d[0].msg : "Something went wrong";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-2 bg-[#010204] border border-[#C9C6C6]/12 rounded-none h-12 focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0 font-normal";
  const labelCls =
    "text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]";

  return (
    <section id="guest" className="py-16 sm:py-24 md:py-32 bg-[#010204]">
      <div className="max-w-[900px] mx-auto px-5 sm:px-6 lg:px-10">
        <ScrollReveal>
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-3">
            — Guest request
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
            Quick request, no account.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-[#C9C6C6] max-w-2xl leading-relaxed">
            Four fields. We&apos;ll connect matched local pros — they&apos;ll
            contact you directly by text or phone.{" "}
            <Link to="/homeowner/signup" className="underline underline-offset-4 text-[#0E93EC]">
              Want to track it and see quotes? Create an account instead.
            </Link>
          </p>
        </ScrollReveal>

        {done ? (
          <div
            className="mt-8 border border-[#0E93EC]/25 bg-[#0E93EC]/5 p-6 sm:p-8 flex items-start gap-3"
            data-testid="guest-success"
          >
            <CheckCircle2 className="w-6 h-6 text-[#0E93EC] mt-0.5 shrink-0" />
            <div>
              <div className="font-editorial text-2xl text-[#EEEEEF] tracking-tight">
                You&apos;re in the queue.
              </div>
              <p className="mt-2 text-[#EEEEEF] text-sm leading-relaxed">
                Matched local pros will reach out shortly — usually within a
                few hours. Want to see all your quotes in one place next time?{" "}
                <Link to="/homeowner/signup" className="text-[#0E93EC] underline underline-offset-4">
                  Create a free account.
                </Link>
              </p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-8 bg-[#010204] border border-[#C9C6C6]/10 p-6 sm:p-8 md:p-10 space-y-5"
            data-testid="guest-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className={labelCls}>Full name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Jane Doe"
                  className={inputCls}
                  data-testid="guest-name"
                  required
                />
              </div>
              <div>
                <Label className={labelCls}>Service needed *</Label>
                <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
                  <SelectTrigger
                    className={inputCls}
                    data-testid="guest-service"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRADES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className={labelCls}>Address *</Label>
                <Input
                  value={form.address}
                  onChange={(e) => set("address", e.target.value)}
                  placeholder="123 Main St, Anytown, ST 12345"
                  className={inputCls}
                  data-testid="guest-address"
                  required
                />
              </div>
              <div>
                <Label className={labelCls}>Phone *</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="(555) 555-5555"
                  className={inputCls}
                  data-testid="guest-phone"
                  required
                />
              </div>
            </div>

            <label className="flex items-start gap-3 text-sm text-[#C9C6C6] leading-relaxed">
              <input
                type="checkbox"
                checked={form.agreed_to_disclaimer}
                onChange={(e) => set("agreed_to_disclaimer", e.target.checked)}
                className="mt-1"
                data-testid="guest-disclaimer"
                required
              />
              <span>
                By submitting, I agree to be contacted by matched pros about
                this project. The Pros Work Hub only connects — pros work directly
                with me on scheduling, pricing, and completion. See{" "}
                <a href="#" className="underline">Terms</a> &{" "}
                <a href="#" className="underline">Privacy</a>.
              </span>
            </label>

            <Button
              type="submit"
              disabled={submitting}
              className="group w-full sm:w-auto bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-6 pr-4 text-base font-medium"
              data-testid="guest-submit"
            >
              <span className="mr-2">{submitting ? "Sending…" : "Send my request"}</span>
              <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

/* ============================================================
 *  Manifesto — numbered chapters, editorial typography
 * ============================================================ */
function Manifesto() {
  const chapters = [
    {
      n: "01",
      title: "We're built for a place, not a nation.",
      body:
        "Angi turns leads into an auction. HomeGuide takes a slice of every job. We charge one flat monthly fee — the same for every pro, coast to coast. No middlemen. No markup on your work.",
    },
    {
      n: "02",
      title: "One home. One pro. One channel.",
      body:
        "The Pros Work Hub is a private workspace, not an auction floor. Once a homeowner accepts an estimate, the job is locked to one pro — with a shared agreement, a private message board, and a clean sign-off at the end.",
    },
    {
      n: "03",
      title: "One flat monthly fee. Forever.",
      body:
        "You'll never pay per lead that ghosts you. Pick Starter or Pro — one flat monthly fee, leads delivered straight to your workspace. Cancel any time.",
    },
    {
      n: "04",
      title: "Neighbors helping neighbors.",
      body:
        "Every pro is background-checked, license-verified, and locally rated. The work is done by people who live down the road.",
    },
  ];

  return (
    <section
      id="manifesto"
      className="relative py-16 sm:py-24 md:py-48 bg-[#0D1421] overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <ScrollReveal>
              <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
                — The Manifesto
              </div>
              <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
                Four beliefs.
                <br />
                <span className="italic">One promise.</span>
              </h2>
              <p className="mt-8 text-[#C9C6C6] max-w-sm leading-relaxed">
                Why we exist, in the shortest possible language.
              </p>
            </ScrollReveal>
          </div>

          <div className="lg:col-span-8 space-y-24 md:space-y-32">
            {chapters.map((c) => (
              <ScrollReveal key={c.n} y={40} className="grid grid-cols-12 gap-6">
                <div className="col-span-2 md:col-span-2">
                  <div className="font-editorial italic text-6xl md:text-7xl font-light text-[#0E93EC] leading-none">
                    {c.n}
                  </div>
                </div>
                <div className="col-span-10 md:col-span-10">
                  <h3 className="font-editorial text-3xl md:text-5xl font-light tracking-tight text-[#EEEEEF] leading-[1.1]">
                    {c.title}
                  </h3>
                  <p className="mt-6 text-lg text-[#C9C6C6] leading-relaxed max-w-2xl">
                    {c.body}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 *  How It Works — clean tabs, editorial numerals
 * ============================================================ */
function HowItWorks() {
  const [tab, setTab] = useState("homeowner");
  const steps = {
    homeowner: [
      { n: "01", t: "Tell us about your project", d: "Answer a few quick questions — service, location, timing." },
      { n: "02", t: "Get matched with up to 3 pros", d: "We hand-pick vetted, local pros that fit your job." },
      { n: "03", t: "Compare & hire", d: "Chat, review portfolios, pick the pro you love. Free forever." },
    ],
    contractor: [
      { n: "01", t: "Create your profile", d: "Add your trade, service area, license, and portfolio in minutes." },
      { n: "02", t: "Subscribe to a plan", d: "Pick Starter or Pro — one flat monthly fee, no per-lead charges." },
      { n: "03", t: "Win work & scale", d: "Real homeowner requests in your area. Cancel anytime." },
    ],
  };
  return (
    <section id="how" className="py-16 sm:py-24 md:py-40 bg-[#010204]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <ScrollReveal>
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
              — How it works
            </div>
            <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] max-w-3xl break-word-safe">
              Simple for homeowners.
              <br />
              <span className="italic">Predictable for pros.</span>
            </h2>
          </ScrollReveal>
          <div className="inline-flex border border-[#C9C6C6]/20 self-start">
            <button
              onClick={() => setTab("homeowner")}
              className={`px-6 py-3 text-xs uppercase tracking-[0.18em] font-medium transition-colors ${
                tab === "homeowner"
                  ? "bg-[#0E93EC] text-white"
                  : "bg-transparent text-[#EEEEEF] hover:bg-[#EEEEEF]/8"
              }`}
              data-testid="how-tab-homeowner"
            >
              For Homeowners
            </button>
            <button
              onClick={() => setTab("contractor")}
              className={`px-6 py-3 text-xs uppercase tracking-[0.18em] font-medium transition-colors border-l border-[#C9C6C6]/20 ${
                tab === "contractor"
                  ? "bg-[#0E93EC] text-white"
                  : "bg-transparent text-[#EEEEEF] hover:bg-[#EEEEEF]/8"
              }`}
              data-testid="how-tab-contractor"
            >
              For Pros
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {steps[tab].map((s, i) => (
            <ScrollReveal
              key={s.n}
              delay={i * 0.1}
              className="bg-[#0D1421] border border-[#C9C6C6]/10 p-6 sm:p-8 md:p-10 hover:shadow-xl transition-shadow group"
            >
              <div
                className="font-editorial italic text-6xl sm:text-7xl md:text-8xl font-light text-[#0E93EC] tracking-tighter leading-none"
                data-testid={`how-step-${s.n}`}
              >
                {s.n}
              </div>
              <h3 className="mt-6 sm:mt-8 font-editorial text-2xl md:text-3xl font-normal text-[#EEEEEF] leading-tight break-word-safe">
                {s.t}
              </h3>
              <p className="mt-3 sm:mt-4 text-[#C9C6C6] leading-relaxed break-word-safe">{s.d}</p>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 *  Popular Services — spotlighted bento with subtle motion
 * ============================================================ */
function PopularServices() {
  const items = [
    { name: "Plumbing", icon: Droplets, span: "md:col-span-8 md:row-span-2", img: SERVICE_IMGS.Plumbing },
    { name: "Electrical", icon: Zap, span: "md:col-span-4", img: SERVICE_IMGS.Electrical },
    { name: "HVAC", icon: Wrench, span: "md:col-span-4", img: SERVICE_IMGS.HVAC },
    { name: "Landscaping", icon: Trees, span: "md:col-span-8", img: SERVICE_IMGS.Landscaping },
    { name: "Roofing", icon: Home, span: "md:col-span-6", img: SERVICE_IMGS.Roofing },
    { name: "Painting", icon: Paintbrush, span: "md:col-span-6", img: SERVICE_IMGS.Painting },
  ];
  return (
    <section id="services" className="py-16 sm:py-24 md:py-40 bg-[#0D1421]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12">
        <ScrollReveal className="mb-16 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
            — Trades we route
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
            Every fix, every project —
            <br />
            <span className="italic">one quiet place.</span>
          </h2>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[240px] gap-4">
          {items.map((s, i) => {
            const Icon = s.icon;
            return (
              <ScrollReveal
                key={s.name}
                delay={i * 0.05}
                y={20}
                className={`relative overflow-hidden group cursor-lift ${s.span}`}
              >
                <div
                  className="relative w-full h-full overflow-hidden"
                  data-testid={`service-card-${s.name.toLowerCase()}`}
                >
                  <img
                    src={s.img}
                    alt={s.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
                  <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-between">
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 bg-[#0D1421] flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#0E93EC]" strokeWidth={2} />
                      </div>
                      <div className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-white/70">
                        0{i + 1}
                      </div>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <div className="font-editorial text-4xl md:text-5xl font-normal text-white tracking-tighter">
                          {s.name}
                        </div>
                        <div className="mt-2 text-white/80 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                          Browse pros
                          <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 *  Homeowner Lead Form (used inline + in dialog)
 * ============================================================ */
function HomeownerLeadForm({ scope = "inline" }) {
  const [form, setForm] = useState({
    service_type: "",
    zip_code: "",
    name: "",
    email: "",
    project_details: "",
  });
  const [wantsMultiple, setWantsMultiple] = useState(true);
  const [agreedReview, setAgreedReview] = useState(false);
  const [agreedDisclaimer, setAgreedDisclaimer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.service_type ||
      !form.zip_code ||
      !form.name ||
      !form.email
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!agreedReview) {
      toast.error("Please agree to leave a review after service is provided.");
      return;
    }
    if (!agreedDisclaimer) {
      toast.error(
        "Please acknowledge that The Pros Work Hub only connects — we take no responsibility after contact."
      );
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        wants_multiple_quotes: wantsMultiple,
        agreed_to_review: agreedReview,
        agreed_to_disclaimer: agreedDisclaimer,
      });
      toast.success("Request sent! Local pros will reach out soon.");
      setDone(true);
      setForm({
        service_type: "",
        zip_code: "",
        name: "",
        email: "",
        project_details: "",
      });
      setWantsMultiple(true);
      setAgreedReview(false);
      setAgreedDisclaimer(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Please check your inputs";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-2 bg-[#010204] border-0 rounded-none h-12 focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0 font-normal";
  const labelCls =
    "text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]";

  return (
    <form
      onSubmit={submit}
      className="space-y-5"
      data-testid={scope === "dialog" ? "homeowner-lead-form-dialog" : "homeowner-lead-form"}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className={labelCls}>Service needed *</Label>
          <Select value={form.service_type} onValueChange={(v) => set("service_type", v)}>
            <SelectTrigger
              className={inputCls + " focus:ring-2 focus:ring-[#0E93EC]"}
              data-testid={scope === "dialog" ? "lead-service-type-dialog" : "lead-service-type"}
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelCls}>
            ZIP code *
          </Label>
          <Input
            value={form.zip_code}
            onChange={(e) => set("zip_code", e.target.value)}
            placeholder="e.g. 10001"
            className={inputCls}
            data-testid={scope === "dialog" ? "lead-zip-dialog" : "lead-zip"}
          />
        </div>
        <div>
          <Label className={labelCls}>Full name *</Label>
          <Input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Jane Doe"
            className={inputCls}
            data-testid={scope === "dialog" ? "lead-name-dialog" : "lead-name"}
          />
        </div>
        <div>
          <Label className={labelCls}>Email *</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="jane@example.com"
            className={inputCls}
            data-testid={scope === "dialog" ? "lead-email-dialog" : "lead-email"}
          />
        </div>
        <div className="md:col-span-2">
          <Label className={labelCls}>Tell us about the project</Label>
          <Textarea
            value={form.project_details}
            onChange={(e) => set("project_details", e.target.value)}
            placeholder="Rough size, timing, any specific concerns…"
            rows={4}
            className="mt-2 bg-[#010204] border-0 rounded-none focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0"
            data-testid={scope === "dialog" ? "lead-details-dialog" : "lead-details"}
          />
        </div>
      </div>

      {/* Multiple quotes toggle */}
      <div>
        <Label className={labelCls}>
          Would you like multiple quotes?{" "}
          <span className="normal-case tracking-normal text-[#C9C6C6] font-normal">
            More quotes = better prices to compare
          </span>
        </Label>
        <div
          className="mt-2 grid grid-cols-2 gap-2"
          data-testid={scope === "dialog" ? "lead-multiple-quotes-dialog" : "lead-multiple-quotes"}
        >
          {[
            { id: true, label: "Yes — send me up to 3 quotes", sub: "Best value" },
            { id: false, label: "No — just one pro is fine", sub: "Fastest" },
          ].map((opt) => {
            const active = wantsMultiple === opt.id;
            return (
              <button
                key={String(opt.id)}
                type="button"
                onClick={() => setWantsMultiple(opt.id)}
                className={`text-left px-4 py-3 border transition-colors ${
                  active
                    ? "bg-[#0E93EC] text-white border-[#0E93EC]"
                    : "bg-transparent text-[#EEEEEF] border-[#C9C6C6]/15 hover:border-[#0E93EC]"
                }`}
                data-testid={`lead-quotes-${opt.id ? "yes" : "no"}${scope === "dialog" ? "-dialog" : ""}`}
              >
                <div className="text-[13px] font-medium">{opt.label}</div>
                <div
                  className={`text-[10px] uppercase tracking-[0.18em] mt-1 ${
                    active ? "text-[#EEEEEF]/70" : "text-[#C9C6C6]"
                  }`}
                >
                  {opt.sub}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Required agreements */}
      <div className="space-y-3 border-t border-[#C9C6C6]/10 pt-5">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
          Before we send your request
        </div>
        <label
          className="flex items-start gap-3 cursor-pointer"
          data-testid={scope === "dialog" ? "lead-agree-review-dialog" : "lead-agree-review"}
        >
          <input
            type="checkbox"
            checked={agreedReview}
            onChange={(e) => setAgreedReview(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#0E93EC] shrink-0 cursor-pointer"
          />
          <span className="text-[13px] text-[#EEEEEF] leading-relaxed">
            <b className="text-[#EEEEEF]">I&apos;ll leave a review</b> after service is
            provided. Reviews help us keep the network professional and ensure
            pros do the job right.
          </span>
        </label>
        <label
          className="flex items-start gap-3 cursor-pointer"
          data-testid={scope === "dialog" ? "lead-agree-disclaimer-dialog" : "lead-agree-disclaimer"}
        >
          <input
            type="checkbox"
            checked={agreedDisclaimer}
            onChange={(e) => setAgreedDisclaimer(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#0E93EC] shrink-0 cursor-pointer"
          />
          <span className="text-[13px] text-[#EEEEEF] leading-relaxed">
            I understand{" "}
            <b className="text-[#EEEEEF]">
              The Pros Work Hub only provides the connection between customer and pro
            </b>
            . The Pros Work Hub takes no responsibility for anything — work quality,
            scheduling, pricing, disputes, or damages — after contact has been
            made.
          </span>
        </label>
      </div>


      <Magnetic strength={5}>
        <Button
          type="submit"
          disabled={submitting || !agreedReview || !agreedDisclaimer}
          className="group w-full md:w-auto bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-8 pr-5 text-base font-medium disabled:opacity-50"
          data-testid={scope === "dialog" ? "lead-submit-dialog" : "lead-submit"}
        >
          <span className="mr-2">{submitting ? "Sending…" : "Get free quotes"}</span>
          <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
            <ArrowRight className="w-4 h-4" />
          </span>
        </Button>
      </Magnetic>

      {done && (
        <div
          className="flex items-start gap-3 text-sm text-[#0E93EC] bg-[#0E93EC]/5 p-4 border-l-2 border-[#0E93EC]"
          data-testid={scope === "dialog" ? "lead-success-dialog" : "lead-success"}
        >
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <b>Request received.</b> We&apos;re matching you with up to 3 local pros.
            You&apos;ll hear from them within 24 hours.
          </div>
        </div>
      )}
    </form>
  );
}

/* ============================================================
 *  Lead Section
 * ============================================================ */
function LeadSection() {
  return (
    <section id="get-quotes" className="py-16 sm:py-24 md:py-40 bg-[#0D1421]">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <ScrollReveal>
              <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
                — For Homeowners
              </div>
              <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95]">
                Up to 3 free quotes,
                <br />
                <span className="italic">from local pros.</span>
              </h2>
              <p className="mt-8 text-lg text-[#C9C6C6] leading-relaxed max-w-md">
                Coast to coast. Tell us about your project — we&apos;ll match you
                with vetted local pros in your ZIP code, usually within
                hours.
              </p>
              <ul className="mt-10 space-y-4 text-[#EEEEEF]">
                {[
                  "100% free for homeowners, forever",
                  "Licensed, insured, and locally reviewed pros only",
                  "No spam. No bidding wars. You choose who to talk to.",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-[15px]">
                    <span className="mt-1.5 w-1.5 h-1.5 bg-[#0E93EC] shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </ScrollReveal>
          </div>
          <ScrollReveal delay={0.15} className="lg:col-span-7 bg-[#010204] border border-[#C9C6C6]/10 p-8 md:p-12 shadow-sm">
            <HomeownerLeadForm />
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 *  Contractor Signup Form
 * ============================================================ */
export function ContractorSignupForm({ scope = "inline", onSuccess }) {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    trade: "",
    email: "",
    phone: "",
    service_area: "",
  });
  const [links, setLinks] = useState([""]);
  const [workZones, setWorkZones] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLink = (i, v) =>
    setLinks((prev) => prev.map((x, idx) => (idx === i ? v : x)));
  const addLink = () =>
    setLinks((prev) => (prev.length >= 5 ? prev : [...prev, ""]));
  const removeLink = (i) =>
    setLinks((prev) =>
      prev.length === 1 ? [""] : prev.filter((_, idx) => idx !== i)
    );
  const toggleZone = (id) =>
    setWorkZones((prev) =>
      prev.includes(id) ? prev.filter((z) => z !== id) : [...prev, id]
    );

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.business_name ||
      !form.trade ||
      !form.email ||
      !form.phone ||
      !form.service_area
    ) {
      toast.error("Please complete all fields");
      return;
    }
    const cleanedLinks = links.map((l) => l.trim()).filter(Boolean);
    if (cleanedLinks.length === 0) {
      toast.error(
        "Add at least one external profile link (Google Business, Facebook, website…)"
      );
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contractors`, {
        ...form,
        external_profile_links: cleanedLinks,
        work_zones: workZones,
      });
      toast.success("Welcome aboard! Pick a plan to start claiming leads.");
      setDone(true);
      setForm({
        name: "",
        business_name: "",
        trade: "",
        email: "",
        phone: "",
        service_area: "",
      });
      setLinks([""]);
      setWorkZones([]);
      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      // pydantic returns list of errors — try to surface the first
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Please check your inputs";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "mt-2 bg-[#010204] border-0 rounded-none h-12 focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0 font-normal";
  const labelCls =
    "text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]";

  return (
    <form
      onSubmit={submit}
      className="space-y-5"
      data-testid={scope === "dialog" ? "contractor-signup-form-dialog" : "contractor-signup-form"}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className={labelCls}>Your name *</Label>
          <Input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="John Smith"
            className={inputCls}
            data-testid={scope === "dialog" ? "contractor-name-dialog" : "contractor-name"}
          />
        </div>
        <div>
          <Label className={labelCls}>Business name *</Label>
          <Input
            value={form.business_name}
            onChange={(e) => set("business_name", e.target.value)}
            placeholder="Smith Plumbing Co."
            className={inputCls}
            data-testid={scope === "dialog" ? "contractor-business-dialog" : "contractor-business"}
          />
        </div>
        <div>
          <Label className={labelCls}>Trade *</Label>
          <Select value={form.trade} onValueChange={(v) => set("trade", v)}>
            <SelectTrigger
              className={inputCls + " focus:ring-2 focus:ring-[#0E93EC]"}
              data-testid={scope === "dialog" ? "contractor-trade-dialog" : "contractor-trade"}
            >
              <SelectValue placeholder="Select your trade" />
            </SelectTrigger>
            <SelectContent>
              {TRADES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelCls}>Service area *</Label>
          <Input
            value={form.service_area}
            onChange={(e) => set("service_area", e.target.value)}
            placeholder="Your city, county, or ZIP"
            className={inputCls}
            data-testid={scope === "dialog" ? "contractor-area-dialog" : "contractor-area"}
          />
        </div>
        <div>
          <Label className={labelCls}>Email *</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="john@company.com"
            className={inputCls}
            data-testid={scope === "dialog" ? "contractor-email-dialog" : "contractor-email"}
          />
        </div>
        <div>
          <Label className={labelCls}>Phone *</Label>
          <Input
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="(555) 555-5555"
            className={inputCls}
            data-testid={scope === "dialog" ? "contractor-phone-dialog" : "contractor-phone"}
          />
        </div>
      </div>

      {/* External profile links — REQUIRED */}
      <div>
        <Label className={labelCls}>
          External profile links *{" "}
          <span className="normal-case tracking-normal text-[#C9C6C6] font-normal">
            Google Business, Facebook, website — at least one
          </span>
        </Label>
        <div className="mt-2 space-y-2" data-testid={scope === "dialog" ? "contractor-links-dialog" : "contractor-links"}>
          {links.map((v, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={v}
                onChange={(e) => setLink(i, e.target.value)}
                placeholder={
                  i === 0
                    ? "g.co/kgs/yourbiz  ·  facebook.com/yourbiz  ·  yourbiz.com"
                    : "Additional profile URL"
                }
                className="bg-[#010204] border-0 rounded-none h-12 focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0 font-normal"
                data-testid={
                  scope === "dialog"
                    ? `contractor-link-${i}-dialog`
                    : `contractor-link-${i}`
                }
              />
              {links.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeLink(i)}
                  className="p-2 text-[#C9C6C6] hover:text-[#EEEEEF]"
                  aria-label="Remove link"
                  data-testid={
                    scope === "dialog"
                      ? `contractor-link-remove-${i}-dialog`
                      : `contractor-link-remove-${i}`
                  }
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {links.length < 5 && (
          <button
            type="button"
            onClick={addLink}
            className="mt-3 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] hover:text-[#010204] inline-flex items-center gap-1 border-b border-[#0E93EC]/40 hover:border-[#0E93EC] pb-0.5"
            data-testid={scope === "dialog" ? "contractor-link-add-dialog" : "contractor-link-add"}
          >
            + Add another link
          </button>
        )}
      </div>

      {/* Where do you want to work? */}
      <div>
        <Label className={labelCls}>
          Where do you want to work?{" "}
          <span className="normal-case tracking-normal text-[#C9C6C6] font-normal">
            Pick all that apply — no home-base limits
          </span>
        </Label>
        <div
          className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2"
          data-testid={scope === "dialog" ? "contractor-workzones-dialog" : "contractor-workzones"}
        >
          {[
            { id: "home", label: "My hometown / immediate area" },
            { id: "25mi", label: "Within 25 miles" },
            { id: "50mi", label: "Within 50 miles" },
            { id: "100mi", label: "Within 100 miles" },
            { id: "statewide", label: "Anywhere in my state" },
            { id: "out_of_state", label: "Out-of-state for the right job" },
          ].map((z) => {
            const active = workZones.includes(z.id);
            return (
              <button
                key={z.id}
                type="button"
                onClick={() => toggleZone(z.id)}
                className={`text-left px-3 py-3 text-[13px] border transition-colors flex items-center gap-2 ${
                  active
                    ? "bg-[#0E93EC] text-white border-[#0E93EC]"
                    : "bg-transparent text-[#EEEEEF] border-[#C9C6C6]/15 hover:border-[#0E93EC]"
                }`}
                data-testid={
                  scope === "dialog"
                    ? `contractor-workzone-${z.id}-dialog`
                    : `contractor-workzone-${z.id}`
                }
              >
                <span
                  className={`inline-block w-3 h-3 shrink-0 ${
                    active
                      ? "bg-[#0E93EC]"
                      : "border border-[#C9C6C6]/40"
                  }`}
                />
                {z.label}
              </button>
            );
          })}
        </div>
      </div>

      <Magnetic strength={5}>
        <Button
          type="submit"
          disabled={submitting}
          className="group w-full md:w-auto bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-8 pr-5 text-base font-medium"
          data-testid={scope === "dialog" ? "contractor-submit-dialog" : "contractor-submit"}
        >
          <span className="mr-2">
            {submitting ? "Creating account…" : "Create my pro account"}
          </span>
          <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
            <ArrowRight className="w-4 h-4" />
          </span>
        </Button>
      </Magnetic>

      {done && (
        <div
          className="flex items-start gap-3 text-sm text-[#0E93EC] bg-[#0E93EC]/5 p-4 border-l-2 border-[#0E93EC]"
          data-testid={scope === "dialog" ? "contractor-success-dialog" : "contractor-success"}
        >
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <b>You&apos;re in.</b> Pick a plan below to start claiming leads.
          </div>
        </div>
      )}
    </form>
  );
}

/* ============================================================
 *  Contractor Section — dark, editorial
 * ============================================================ */
function ContractorSection() {
  return (
    <section
      id="contractors"
      className="relative py-16 sm:py-24 md:py-40 bg-[#010204] text-[#EEEEEF] grain overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-16 items-start relative z-10">
        <div className="lg:col-span-5">
          <ScrollReveal>
            <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
              — For Pros
            </div>
            <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#EEEEEF] leading-[0.9] break-word-safe">
              You choose
              <br />
              where you work.
              <br />
              <span className="italic">We bring the work to you.</span>
            </h2>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg text-[#C9C6C6] leading-relaxed max-w-md">
              Stop paying $30-$80 per lead to national platforms. Set your own
              travel zones — The Pros Work Hub delivers matching leads straight to
              your Kanban workspace with in-app messaging, estimates, agreements
              and a public profile that showcases your reviews. From $40/mo.
              Cancel any time.
            </p>
            <div className="mt-8 sm:mt-12 space-y-4 sm:space-y-5">
              {[
                "You pick your radius — hometown, 50 mi, statewide, out-of-state",
                "Flat monthly rate, no bidding, no per-lead fees",
                "One private workspace per job — agreement, chat, sign-off",
                "Cancel anytime — month-to-month",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-4 group">
                  <span className="mt-2.5 w-2 h-2 bg-[#0E93EC] shrink-0" />
                  <span className="text-[#C9C6C6] text-[15px]">{t}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal delay={0.15} className="lg:col-span-7 bg-[#0D1421] text-[#EEEEEF] p-6 sm:p-8 md:p-12">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-4">
            — Ready to join?
          </div>
          <h3 className="font-editorial text-3xl sm:text-4xl md:text-5xl font-light tracking-tighter leading-[0.95] break-word-safe">
            Two minutes to set up.
            <br /><span className="italic">Leads land right after.</span>
          </h3>
          <p className="mt-6 text-[#C9C6C6] leading-relaxed text-sm sm:text-base">
            Create your pro account, choose your service zones, and pick a
            Starter or Pro plan. You&apos;ll be seeing local requests today.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link
              to="/pro/signup"
              className="group inline-flex items-center bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-6 pr-4 text-base font-medium justify-between"
              data-testid="forpros-signup-cta"
            >
              <span className="mr-2">Sign up as a Pro</span>
              <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
            <Link
              to="/pro/login"
              className="inline-flex items-center justify-center h-14 px-5 border border-[#C9C6C6]/15 hover:border-[#C9C6C6] text-[#EEEEEF] uppercase tracking-[0.15em] text-xs font-semibold"
              data-testid="forpros-login-cta"
            >
              Already a pro? Log in
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ============================================================
 *  Subscribe Dialog — collects email and redirects to Stripe
 * ============================================================ */
function SubscribeDialog({ open, onOpenChange, planId }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Enter your email to continue");
      return;
    }
    if (!planId) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/checkout/session`, {
        plan_id: planId,
        email,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Payment error");
      setLoading(false);
    }
  };

  const planName =
    planId === "starter" ? "Starter · $40/mo"
    : planId === "pro" ? "Pro · $60/mo"
    : planId === "team" ? "Team · $99/mo"
    : planId === "founding_starter" ? "Founding Starter · $25/mo (locked for life)"
    : planId === "founding_pro" ? "Founding Pro · $45/mo (locked for life)"
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md rounded-none bg-[#0D1421] p-8 border-[#C9C6C6]/10"
        data-testid="subscribe-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-editorial text-3xl font-light text-[#EEEEEF] tracking-tighter">
            Subscribe — <span className="italic">{planName}</span>
          </DialogTitle>
          <DialogDescription className="text-[#C9C6C6]">
            Enter the email you use for your business. You&apos;ll be redirected to
            Stripe to complete payment securely.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="subscribe-form">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
              Business email *
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              className="mt-2 bg-[#010204] border-0 rounded-none h-12 focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0"
              data-testid="subscribe-email"
              autoFocus
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="group w-full bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-8 pr-5 text-base font-medium"
            data-testid="subscribe-submit"
          >
            <span className="mr-2">
              {loading ? "Redirecting to Stripe…" : "Continue to secure checkout"}
            </span>
            <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] transition-transform group-hover:translate-x-1">
              <ArrowRight className="w-4 h-4" />
            </span>
          </Button>
          <p className="text-[11px] text-[#C9C6C6] leading-relaxed">
            Secure payment powered by Stripe · Cancel anytime · Test mode
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================
 *  Pricing
 * ============================================================ */
/* ============================================================
 *  FoundingMember — live counter + two founder-tier cards
 *  Auto-hides once all 20 seats are claimed.
 * ============================================================ */
function FoundingMember({ onSubscribe }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/founding/status`);
        if (alive) setStatus(data);
      } catch {
        /* silent */
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Hide entirely when all seats are claimed or during first fetch.
  if (loading || !status || !status.is_open) return null;

  const remaining = status.remaining;
  const claimed = status.claimed;
  const cap = status.cap;
  const progress = Math.min(100, Math.round((claimed / cap) * 100));

  const tiers = [
    {
      id: "founding_starter",
      name: "Founding Starter",
      price: "$25",
      regular: "$40",
      period: "per month · locked for life",
      features: [
        "Everything in Starter",
        "Unlimited work opportunities",
        "Founding Member badge on your profile",
        "Rate never increases — locked forever",
      ],
    },
    {
      id: "founding_pro",
      name: "Founding Pro",
      price: "$45",
      regular: "$60",
      period: "per month · locked for life",
      features: [
        "Everything in Pro",
        "Unlimited work opportunities",
        "Founding Member badge on your profile",
        "$45/mo forever — save $180/year",
      ],
      highlight: true,
    },
  ];

  return (
    <section
      id="founding"
      className="relative py-16 sm:py-24 md:py-32 bg-[#010204] text-[#EEEEEF] overflow-hidden grain"
      data-testid="founding-section"
    >
      {/* Subtle gold glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-gradient-radial from-[#C9C6C6]/15 via-transparent to-transparent blur-3xl" />
      </div>

      <div className="relative max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12">
        <ScrollReveal className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#C9C6C6]/15 border border-[#C9C6C6]/40 text-[#C9C6C6] text-[10px] uppercase tracking-[0.25em] font-semibold mb-6"
            data-testid="founding-badge-label"
          >
            <Star className="w-3 h-3 fill-[#C9C6C6]" /> Founding Member
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] break-word-safe">
            First 20 pros in.
            <br />
            <span className="italic text-[#C9C6C6]">Locked-in pricing.</span>
          </h2>
          <p className="mt-6 sm:mt-8 text-base sm:text-lg text-[#C9C6C6] max-w-xl mx-auto leading-relaxed">
            Founding rates never increase. Ever. As long as you stay subscribed,
            you keep this price for life.
          </p>

          {/* Live counter */}
          <div className="mt-10 max-w-md mx-auto" data-testid="founding-counter">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <span className="font-editorial text-5xl sm:text-6xl font-light tracking-tighter text-[#C9C6C6]">
                  {remaining}
                </span>
                <span className="ml-2 text-sm uppercase tracking-[0.22em] text-[#C9C6C6]">
                  of {cap} spots remaining
                </span>
              </div>
            </div>
            <div className="h-2 bg-[#0D1421]/10 relative overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-[#C9C6C6]"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
              {claimed} claimed · once we hit {cap}, founding rates disappear forever
            </div>
          </div>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-6 max-w-3xl mx-auto">
          {tiers.map((t, i) => {
            const isHighlight = t.highlight;
            return (
              <ScrollReveal
                key={t.id}
                delay={i * 0.1}
                className={`relative p-6 sm:p-8 md:p-10 flex flex-col border ${
                  isHighlight
                    ? "bg-[#C9C6C6]/8 border-[#C9C6C6] shadow-2xl md:-translate-y-3"
                    : "bg-[#0D1421]/5 border-[#C9C6C6]/15"
                }`}
                data-testid={`founding-tier-${t.id}`}
              >
                {isHighlight && (
                  <div className="absolute -top-3 left-6 bg-[#C9C6C6] text-[#EEEEEF] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5">
                    Best value
                  </div>
                )}
                <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-3 ${isHighlight ? "text-[#C9C6C6]" : "text-[#C9C6C6]"}`}>
                  {t.name}
                </div>
                <div className="flex items-baseline gap-3">
                  <span className={`font-editorial text-6xl md:text-7xl font-light tracking-tighter ${isHighlight ? "text-[#C9C6C6]" : "text-[#EEEEEF]"}`}>
                    {t.price}
                  </span>
                  <span className="text-lg text-[#C9C6C6] line-through" data-testid={`founding-strike-${t.id}`}>
                    {t.regular}
                  </span>
                </div>
                <div className="mt-1 text-xs uppercase tracking-[0.22em] text-[#C9C6C6]">
                  {t.period}
                </div>

                <ul className="mt-6 sm:mt-8 space-y-3 flex-1">
                  {t.features.map((f, k) => (
                    <li key={k} className="flex items-start gap-3 text-sm text-[#C9C6C6]">
                      <Plus
                        className={`w-3.5 h-3.5 mt-1 shrink-0 ${isHighlight ? "text-[#C9C6C6]" : "text-[#0E93EC]"}`}
                        strokeWidth={2}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => onSubscribe(t.id)}
                  className={`mt-8 rounded-none h-12 font-semibold ${
                    isHighlight
                      ? "bg-[#C9C6C6] hover:bg-[#C9C6C6] text-[#EEEEEF]"
                      : "bg-[#0D1421] hover:bg-[#EEEEEF]/10 text-[#EEEEEF]"
                  }`}
                  data-testid={`founding-cta-${t.id}`}
                >
                  Claim {t.name}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </ScrollReveal>
            );
          })}
        </div>

        <div className="mt-10 text-center text-xs uppercase tracking-[0.22em] text-[#C9C6C6]">
          Cancel any time · Cancellation forfeits founding rate permanently
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 *  Pricing — flat tiers with subtle grain background
 * ============================================================ */
function Pricing({ onSubscribe }) {
  const plans = [
    {
      id: "starter",
      name: "Starter",
      price: "$40",
      period: "per month · billed monthly",
      description:
        "The complete TPWH system for an individual pro — find work, manage jobs, get paid.",
      features: [
        "Unlimited work opportunities",
        "Trade-based job search",
        "Full CRM: estimates, scheduling, photos, time tracking, invoices, notes",
        "Job calendar",
        "Business profile + reviews",
      ],
      cta: "Subscribe · Starter",
      testid: "pricing-starter",
    },
    {
      id: "pro",
      name: "Pro",
      price: "$60",
      period: "per month · billed monthly",
      description:
        "Everything in Starter, plus the business-management tools as they roll out.",
      features: [
        "Everything in Starter",
        "Advanced CRM tools as they roll out — reporting, estimate templates, customer history",
        "Customer follow-up tools",
        "Early access to new Pro features",
      ],
      cta: "Subscribe · Pro",
      testid: "pricing-pro",
      highlight: true,
    },
  ];

  return (
    <section id="pricing" className="py-16 sm:py-24 md:py-40 bg-[#0D1421]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-5 sm:px-6 lg:px-12">
        <ScrollReveal className="text-center mb-12 sm:mb-16 md:mb-20 max-w-3xl mx-auto">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-4 sm:mb-6">
            — Pricing for pros
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
            Flat pricing.
            <br />
            <span className="italic">No surprises.</span>
          </h2>
          <p className="mt-6 sm:mt-8 text-base sm:text-lg text-[#C9C6C6] max-w-xl mx-auto leading-relaxed">
            One flat monthly fee. Every plan is month-to-month, cancel any time.
          </p>
        </ScrollReveal>

        <div className="grid md:grid-cols-2 max-w-4xl mx-auto gap-6">
          {plans.map((p, i) => {
            const isHighlight = p.highlight;
            return (
              <ScrollReveal
                key={p.name}
                delay={i * 0.1}
                className={`relative p-6 sm:p-8 md:p-12 flex flex-col ${
                  isHighlight
                    ? "bg-[#0E2F5F] text-[#EEEEEF] shadow-2xl md:-translate-y-6 border border-[#0E93EC]/50"
                    : "bg-[#010204] border border-[#C9C6C6]/10 text-[#EEEEEF]"
                }`}
              >
                <div data-testid={p.testid} className="flex flex-col h-full">
                  {isHighlight && (
                    <div className="absolute -top-3 left-8 bg-[#0E93EC] text-white text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1.5">
                      Most popular
                    </div>
                  )}
                  <div className="text-[10px] uppercase tracking-[0.22em] font-semibold opacity-80">
                    {p.name}
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-editorial text-6xl md:text-7xl font-light tracking-tighter">
                      {p.price}
                    </span>
                    <span
                      className={`text-sm ${
                        isHighlight ? "text-[#EEEEEF]/60" : "text-[#C9C6C6]"
                      }`}
                    >
                      /mo
                    </span>
                  </div>
                  <div
                    className={`mt-1 text-xs uppercase tracking-[0.15em] ${
                      isHighlight ? "text-[#EEEEEF]/60" : "text-[#C9C6C6]"
                    }`}
                  >
                    {p.period}
                  </div>
                  <p
                    className={`mt-6 text-[15px] leading-relaxed ${
                      isHighlight ? "text-[#EEEEEF]/85" : "text-[#C9C6C6]"
                    }`}
                  >
                    {p.description}
                  </p>
                  <ul className="mt-10 space-y-3 flex-1">
                    {p.features.map((f, k) => (
                      <li key={k} className="flex items-start gap-3 text-sm">
                        <Plus
                          className={`w-3.5 h-3.5 mt-1 shrink-0 ${
                            isHighlight ? "text-[#0E93EC]" : "text-[#0E93EC]"
                          }`}
                          strokeWidth={2}
                        />
                        <span
                          className={
                            isHighlight
                              ? "text-[#EEEEEF]/90"
                              : "text-[#EEEEEF]"
                          }
                        >
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => onSubscribe(p.id)}
                    className={`mt-12 rounded-none h-12 font-medium ${
                      isHighlight
                        ? "bg-[#0E93EC] hover:bg-[#0E2F5F] text-white"
                        : "bg-[#010204] hover:bg-[#C9C6C6] text-white"
                    }`}
                    data-testid={`${p.testid}-cta`}
                  >
                    {p.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay={0.35} className="mt-14 text-center">
          <p className="text-xs uppercase tracking-[0.22em] font-mono-alt text-[#C9C6C6]">
            No pay-per-lead · Month-to-month · Cancel any time
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ============================================================
 *  Testimonials — masonry with staggered offset
 * ============================================================ */
function Testimonials() {
  const items = [
    {
      quote:
        "We found a plumber the same afternoon. Fair quote, clean work — this is how it should be.",
      name: "Sarah Miller",
      role: "Homeowner · Denver, CO",
      img: TESTIMONIAL_IMG_1,
    },
    {
      quote:
        "Booked 6 jobs in my first month on the Pro plan. Best money I've ever spent on marketing.",
      name: "Marcus T.",
      role: "Owner · Blue Ridge Electric",
      img: HERO_IMG_1,
    },
    {
      quote:
        "No more paying $50 for a lead that ghosts you. Flat monthly makes so much more sense.",
      name: "Diego Ramirez",
      role: "Owner · Ramirez Handyman",
      img: HERO_IMG_2,
    },
    {
      quote:
        "Three quotes in an hour. I hired a landscaper Saturday and the yard was done by Wednesday.",
      name: "Priya K.",
      role: "Homeowner · Portland, OR",
      img: TESTIMONIAL_IMG_2,
    },
  ];
  return (
    <section className="py-16 sm:py-24 md:py-40 bg-[#010204]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12">
        <ScrollReveal className="mb-16 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
            — Field notes
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
            Loved by homeowners
            <br />
            <span className="italic">and pros alike.</span>
          </h2>
        </ScrollReveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((t, i) => (
            <ScrollReveal
              key={i}
              delay={i * 0.08}
              className={`bg-[#0D1421] border border-[#C9C6C6]/10 p-8 flex flex-col justify-between min-h-[340px] ${
                i % 3 === 0 ? "md:translate-y-8" : ""
              }`}
            >
              <div data-testid={`testimonial-${i}`}>
                <div className="font-editorial text-6xl italic text-[#0E93EC] leading-none">
                  &ldquo;
                </div>
                <p className="mt-2 font-editorial text-xl text-[#EEEEEF] leading-snug">
                  {t.quote}
                </p>
              </div>
              <div className="mt-8 flex items-center gap-3 pt-6 border-t border-[#C9C6C6]/10">
                <div className="w-10 h-10 rounded-full bg-[#EEEEEF]/12 overflow-hidden">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#EEEEEF]">
                    {t.name}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9C6C6]">
                    {t.role}
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
 *  FAQ
 * ============================================================ */
function FAQ() {
  const faqs = [
    {
      q: "Is The Pros Work Hub really free for homeowners?",
      a: "Yes. Homeowners never pay a cent. Getting quotes, comparing pros, and hiring is 100% free — always.",
    },
    {
      q: "How much does it cost pros?",
      a: "One flat monthly fee — Starter is $40/mo, Pro is $60/mo. No per-lead charges, no bidding wars, no surprises. Cancel any time.",
    },
    {
      q: "How is this different from Angi or HomeGuide?",
      a: "Instead of paying $30-$80 per lead (even if they don't respond), you pay one predictable monthly fee. No bidding wars, no per-lead surprises — plus every pro gets a full workspace with a Kanban lead board, in-app messaging with photo uploads, auto-generated agreements and invoices, and a public profile that showcases verified reviews.",
    },
    {
      q: "Can I cancel my subscription anytime?",
      a: "Absolutely. Every plan is month-to-month. No contracts, no cancellation fees.",
    },
    {
      q: "Are pros vetted?",
      a: "Every pro is background-checked, license-verified where applicable, and rated by real homeowners after each job.",
    },
    {
      q: "What areas do you serve?",
      a: "Homeowners and pros nationwide across the United States. Enter your ZIP code and we'll match you with local vetted pros in your area.",
    },
  ];
  return (
    <section className="py-16 sm:py-24 md:py-40 bg-[#0D1421]">
      <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-12">
        <ScrollReveal className="mb-16">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
            — Questions, answered
          </div>
          <h2 className="font-editorial text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95] break-word-safe">
            Frequently
            <br />
            <span className="italic">Asked.</span>
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-[#C9C6C6]/15"
              >
                <AccordionTrigger
                  className="py-6 sm:py-8 text-left font-editorial text-xl sm:text-2xl md:text-3xl font-light text-[#EEEEEF] hover:no-underline hover:text-[#0E93EC] break-word-safe"
                  data-testid={`faq-q-${i}`}
                >
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 sm:pb-8 text-[#C9C6C6] text-sm sm:text-base leading-relaxed max-w-2xl">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ============================================================
 *  CTA Strip
 * ============================================================ */
function CTAStrip({ onOpenHomeowner, onOpenContractor }) {
  return (
    <section className="relative py-16 sm:py-20 md:py-32 bg-[#0E2F5F] text-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 grid md:grid-cols-2 gap-10 items-center relative z-10">
        <ScrollReveal>
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-white/80 mb-4">
            — Ready?
          </div>
          <h3 className="font-editorial text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95]">
            Ready to fix it —
            <br />
            or <span className="italic">grow it?</span>
          </h3>
        </ScrollReveal>
        <ScrollReveal delay={0.15} className="flex flex-col sm:flex-row gap-4 md:justify-end">
          <Magnetic strength={8}>
            <Button
              onClick={onOpenHomeowner}
              className="group bg-[#010204] text-[#0E93EC] hover:bg-[#EEEEEF]/8 rounded-none h-14 pl-7 pr-5 font-medium"
              data-testid="cta-strip-homeowner"
            >
              <span className="mr-2">I need a pro</span>
              <span className="inline-flex items-center justify-center w-9 h-9 bg-[#0E93EC] text-white transition-transform group-hover:translate-x-1">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </Magnetic>
          <Magnetic strength={8}>
            <Button
              onClick={onOpenContractor}
              className="group bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-14 pl-7 pr-5 font-medium"
              data-testid="cta-strip-contractor"
            >
              <span className="mr-2">I am a pro</span>
              <span className="inline-flex items-center justify-center w-9 h-9 bg-[#010204] text-[#0E93EC] transition-transform group-hover:translate-x-1">
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </Magnetic>
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ============================================================
 *  Footer — massive editorial signature
 * ============================================================ */
function Footer() {
  return (
    <footer className="relative bg-[#010204] text-[#EEEEEF] pt-24 md:pt-32 pb-10 grain overflow-hidden border-t-2 border-[#0E93EC]/40">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 relative z-10">
        <ScrollReveal className="mb-20">
          <h4 className="font-editorial text-[14vw] md:text-[13vw] lg:text-[11rem] font-light tracking-[-0.05em] leading-[0.9] break-words">
            The Pros
            <span className="text-[#0E93EC]">·</span>
            Work Hub
          </h4>
        </ScrollReveal>
        <div className="grid lg:grid-cols-12 gap-10 pb-16 border-t border-[#C9C6C6]/15 pt-12">
          <div className="lg:col-span-5">
            <p className="text-[#EEEEEF]/70 max-w-md leading-relaxed">
              The local home-services network built for both sides of the door.
              Coast to coast.
            </p>
          </div>
          <div className="lg:col-span-4 grid grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-4">
                Homeowners
              </div>
              <ul className="space-y-3 text-sm text-[#EEEEEF]/80">
                <li>
                  <a href="#get-quotes" className="link-sweep hover:text-white">
                    Get quotes
                  </a>
                </li>
                <li>
                  <a href="#services" className="link-sweep hover:text-white">
                    Browse services
                  </a>
                </li>
                <li>
                  <a href="#how" className="link-sweep hover:text-white">
                    How it works
                  </a>
                </li>
                <li>
                  <Link to="/homeowner/login" className="link-sweep hover:text-white" data-testid="footer-homeowner-login">
                    Homeowner Login
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-4">
                Pros
              </div>
              <ul className="space-y-3 text-sm text-[#EEEEEF]/80">
                <li>
                  <a href="#contractors" className="link-sweep hover:text-white">
                    Join as a pro
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="link-sweep hover:text-white">
                    Pricing
                  </a>
                </li>
                <li>
                  <a href="#manifesto" className="link-sweep hover:text-white">
                    Manifesto
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-4">
              Contact
            </div>
            <ul className="space-y-3 text-sm text-[#EEEEEF]/80">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" /> <a href="mailto:hello@theprosworkhub.com" className="hover:text-white break-all">hello@theprosworkhub.com</a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" /> <a href="tel:+16058619908" className="hover:text-white">(605) 861-9908</a>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Nationwide · United States
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[11px] uppercase tracking-[0.2em] font-mono-alt text-[#EEEEEF]/60">
          <div>© {new Date().getFullYear()} The Pros Work Hub · Nationwide</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Pro Agreement</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================
 *  Landing Page (root)
 * ============================================================ */
export default function Landing() {
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribePlan, setSubscribePlan] = useState(null);

  const openSubscribe = (planId) => {
    setSubscribePlan(planId);
    setSubscribeOpen(true);
  };

  useEffect(() => {
    axios.get(`${API}/`).catch(() => {});
  }, []);

  return (
    <SmoothScroll>
      <div className="min-h-screen bg-[#0D1421] text-[#EEEEEF] overflow-x-hidden">
        <SiteHeader />
        <TradesMarquee />
        <Hero />
        <ChoosePath />
        <GuestQuickRequest />
        <Manifesto />
        <HowItWorks />
        <PopularServices />
        <ContractorSection />
        <FoundingMember onSubscribe={openSubscribe} />
        <Pricing onSubscribe={openSubscribe} />
        <Testimonials />
        <FAQ />
        <Footer />

        <SubscribeDialog
          open={subscribeOpen}
          onOpenChange={setSubscribeOpen}
          planId={subscribePlan}
        />
      </div>
    </SmoothScroll>
  );
}
